import { Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { SubscriptionLike } from 'rxjs';

import {
  Dietitian,
  DietitianProps,
} from '../../../../data/dietitian/domain/dietitian';
import { Subscription } from '../../../../data/dietitian/domain/subscription/subscription';
import { MsdpToken } from '../../../../data/msdp/domain/msdp-token';
import { MsdpTokenStateProvider } from '../../../../data/msdp/domain/msdp-token-state.provider';
import { DashboardService } from '../../dashboard.service';
import { SideBarItem, SideBarNavigationTabs } from './sidebar.model';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() dietitian?: Dietitian | undefined;
  @Output() signOut = new EventEmitter();
  @Output() wrongConditionEvent = new EventEmitter<SideBarNavigationTabs>();

  public items: SideBarItem[] = [];
  public msdpSideBarItem: SideBarItem;
  public adminSectionItem: SideBarItem | undefined;
  public activeTab: SideBarNavigationTabs;
  private accountSubscription: SubscriptionLike;
  private msdpToken: MsdpToken | undefined;
  private msdpTokenStateSubscription: SubscriptionLike;
  private first = true;

  public get isAccountActive() {
    return this.activeTab == SideBarNavigationTabs.ACCOUNT;
  }

  public get isBoActive() {
    return this.activeTab == SideBarNavigationTabs.BACK_OFFICE;
  }

  constructor(
    private location: Location,
    private router: Router,
    private dashboardService: DashboardService,
    private msdpTokenStateProvider: MsdpTokenStateProvider,
  ) {
    // Nada
  }

  ngOnInit(): void {
    this.location.onUrlChange(() => {
      this.getCurrentTab();
    });

    this.getCurrentTab();

    this.msdpTokenStateSubscription =
      this.msdpTokenStateProvider.state$.subscribe(async (state) => {
        this.msdpToken = state.data;
        this.msdpSideBarItem = await this.getMsdpSideBarItem(this.dietitian);
      });

    this.adminSectionItem = this.getAdminSectionItem(this.dietitian);
  }

  ngOnDestroy(): void {
    this.accountSubscription?.unsubscribe();
    this.msdpTokenStateSubscription?.unsubscribe();
  }

  goToDashboard() {
    this.router
      .navigate(['/', 'dashboard', this.getMenuItems()[0].navigateTo])
      .then();
  }

  getCurrentTab() {
    const location = this.location.path()?.split('/')[2]?.split('?')[0];
    switch (location) {
      case 'questions':
        this.activeTab = SideBarNavigationTabs.QUESTIONS;
        break;
      case 'templates':
        this.activeTab = SideBarNavigationTabs.TEMPLATES;
        break;
      case 'documents':
        this.activeTab = SideBarNavigationTabs.DOCUMENTS;
        break;
      case 'groups':
        this.activeTab = SideBarNavigationTabs.GROUPS;
        break;
      case 'trackers':
        this.activeTab = SideBarNavigationTabs.TRACKERS;
        break;
      case 'mon-suivi-diet-plus':
        this.activeTab = SideBarNavigationTabs.MON_SUIVI_DIET_PLUS;
        break;
      case 'account':
        this.activeTab = SideBarNavigationTabs.ACCOUNT;
        break;
      case 'nutritions':
        this.activeTab = SideBarNavigationTabs.NUTRITIONS;
        break;
      case 'billing':
        this.activeTab = SideBarNavigationTabs.BILLING;
        break;
      case 'bo':
        this.activeTab = SideBarNavigationTabs.BACK_OFFICE;
        break;
      default:
        this.activeTab = SideBarNavigationTabs.QUESTIONS;
        break;
    }

    this.items = this.getMenuItems();
  }

  getMenuItems() {
    return [
      <SideBarItem>{
        icon: 'iconly-Home',
        label: 'Tableau de bord',
        selected: this.activeTab == SideBarNavigationTabs.QUESTIONS,
        tabValue: SideBarNavigationTabs.QUESTIONS,
        navigateTo: 'questions',
        condition: () => true,
        onlyDesktop: false,
      },
      <SideBarItem>{
        icon: 'iconly-Edit',
        label: 'Templates de notes',
        selected: this.activeTab == SideBarNavigationTabs.TEMPLATES,
        tabValue: SideBarNavigationTabs.TEMPLATES,
        navigateTo: 'templates',
        condition: () => true,
        onlyDesktop: true,
      },
      <SideBarItem>{
        icon: 'iconly-Filter',
        label: 'Trackers',
        selected: this.activeTab == SideBarNavigationTabs.TRACKERS,
        tabValue: SideBarNavigationTabs.TRACKERS,
        navigateTo: 'trackers',
        condition: () => true,
        onlyDesktop: true,
      },
      <SideBarItem>{
        icon: 'iconly-User3',
        label: 'Groupes',
        selected: this.activeTab == SideBarNavigationTabs.GROUPS,
        tabValue: SideBarNavigationTabs.GROUPS,
        navigateTo: 'groups',
        condition: () => true,
        onlyDesktop: true,
      },
      <SideBarItem>{
        icon: 'iconly-Info-Circle',
        label: 'Ressources',
        selected: this.activeTab == SideBarNavigationTabs.USER_GUIDE,
        tabValue: SideBarNavigationTabs.USER_GUIDE,
        url: 'https://www.ressource.monsuividiet.com',
        condition: this.userGuideCondition,
        onlyDesktop: true,
      },
      <SideBarItem>{
        icon: 'iconly-Graph',
        label: 'Nutrition',
        selected: this.activeTab == SideBarNavigationTabs.NUTRITIONS,
        tabValue: SideBarNavigationTabs.NUTRITIONS,
        navigateTo: 'nutritions',
        condition: () => true,
        onlyDesktop: true,
      },
      <SideBarItem>{
        icon: 'iconly-Document',
        label: 'Facturation',
        selected: this.activeTab == SideBarNavigationTabs.BILLING,
        tabValue: SideBarNavigationTabs.BILLING,
        navigateTo: 'billing',
        condition: () => true,
        onlyDesktop: true,
      },
    ];
  }

  async getMsdpSideBarItem(dietitian: Dietitian | undefined) {
    if (
      dietitian &&
      this.msdpToken &&
      this.msdpToken.token &&
      this.msdpToken.userId
    ) {
      if (dietitian && !dietitian.msdpId) {
        dietitian = dietitian.copyWith({
          msdpId: this.msdpToken.userId,
        } as DietitianProps);
      }
      let url;
      try {
        url = await this.dashboardService.link(dietitian);
      } catch (e) {
        if (this.first) {
          this.first = false;
          await this.dashboardService.login(dietitian);
          url = await this.dashboardService.link(dietitian);
        } else {
          url = undefined;
        }
      }
      return <SideBarItem>{
        icon: 'iconly-Video',
        label: 'MonSuiviDiet Plus',
        selected: this.activeTab == SideBarNavigationTabs.MON_SUIVI_DIET_PLUS,
        tabValue: SideBarNavigationTabs.MON_SUIVI_DIET_PLUS,
        url,
        condition: () => true,
        onlyDesktop: true,
      };
    } else {
      return <SideBarItem>{
        icon: 'iconly-Video',
        label: 'MonSuiviDiet Plus',
        selected: this.activeTab == SideBarNavigationTabs.MON_SUIVI_DIET_PLUS,
        tabValue: SideBarNavigationTabs.MON_SUIVI_DIET_PLUS,
        navigateTo: 'account/msdpLink',
        condition: () => true,
        onlyDesktop: true,
      };
    }
  }

  getAdminSectionItem(dietitian: Dietitian | undefined) {
    if (dietitian && dietitian.isAdmin && dietitian.isAdmin === true) {
      return <SideBarItem>{
        icon: 'iconly-Shield-Done',
        label: 'Back-Office',
        selected: this.activeTab == SideBarNavigationTabs.BACK_OFFICE,
        tabValue: SideBarNavigationTabs.BACK_OFFICE,
        navigateTo: 'bo',
        condition: () => true,
        onlyDesktop: true,
      };
    }
    return undefined;
  }

  userGuideCondition(subscription: Subscription | undefined): boolean {
    if (!subscription || !subscription?.isActive) {
      return false;
    } else {
      if (subscription.isTrialing) {
        return false;
      }
    }
    return true;
  }

  onChange(item: SideBarItem) {
    if (!item.condition(this.dietitian?.subscription)) {
      this.wrongConditionEvent.emit(item.tabValue);
    } else {
      if (item.navigateTo) {
        const commands = ['/', 'dashboard'];
        item.navigateTo.split('/').forEach((part) => commands.push(part));
        this.router.navigate(commands).then();
      }

      if (item.url) {
        window.open(item.url, '_blank');
      }
    }
  }

  onAccountSettings() {
    this.router.navigate(['/', 'dashboard', 'account']).then();
  }
}
